import { useState, useMemo } from "react";
import { useApi, useLocation } from "./lib/hooks";
import { getDistance } from "./lib/data";
import type { PrinterProfile } from "./lib/data";
import { MapView } from "./components/MapView";
import { PrinterCard } from "./components/PrinterCard";
import { PrinterProfilePage } from "./components/PrinterProfilePage";
import { OrderPage } from "./components/OrderPage";
import { OrderTracking } from "./components/OrderTracking";
import { ReviewForm } from "./components/ReviewForm";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { Card, CardContent } from "./components/ui/card";
import { TooltipProvider } from "./components/ui/tooltip";
import {
	Search,
	MapPin,
	Package,
	Printer,
	SlidersHorizontal,
	X,
	Layers,
	Star,
	Zap,
	Shield,
	Menu,
	Box,
} from "lucide-react";
import "./index.css";

type Page =
	| { type: "discover" }
	| { type: "printer"; id: string }
	| { type: "order"; printerId: string }
	| { type: "orders" }
	| { type: "review"; orderId: string; printerId: string };

type SortBy = "distance" | "rating" | "price" | "reputation";

export function App() {
	const [page, setPage] = useState<Page>({ type: "discover" });
	const [search, setSearch] = useState("");
	const [selectedPrinter, setSelectedPrinter] = useState<string | null>(null);
	const [materialFilter, setMaterialFilter] = useState<string[]>([]);
	const [sortBy, setSortBy] = useState<SortBy>("distance");
	const [showFilters, setShowFilters] = useState(false);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

	const { data: printers, loading } = useApi<PrinterProfile[]>("/api/printers");
	const { location } = useLocation();

	const allMaterials = useMemo(() => {
		if (!printers) return [];
		const mats = new Set<string>();
		printers.forEach((p) => p.materials.forEach((m) => mats.add(m)));
		return Array.from(mats).sort();
	}, [printers]);

	const filteredPrinters = useMemo(() => {
		if (!printers) return [];
		let filtered = [...printers];

		if (search) {
			const q = search.toLowerCase();
			filtered = filtered.filter(
				(p) =>
					p.name.toLowerCase().includes(q) ||
					p.ownerName.toLowerCase().includes(q) ||
					p.printerModel.toLowerCase().includes(q) ||
					p.materials.some((m) => m.toLowerCase().includes(q)) ||
					p.specialties.some((s) => s.toLowerCase().includes(q))
			);
		}

		if (materialFilter.length > 0) {
			filtered = filtered.filter((p) =>
				materialFilter.every((m) => (p.materials as string[]).includes(m))
			);
		}

		filtered.sort((a, b) => {
			switch (sortBy) {
				case "distance":
					if (!location) return 0;
					return (
						getDistance(location.lat, location.lng, a.location.lat, a.location.lng) -
						getDistance(location.lat, location.lng, b.location.lat, b.location.lng)
					);
				case "rating":
					return b.rating - a.rating;
				case "reputation":
					return b.reputationScore - a.reputationScore;
				case "price": {
					const aMin = Math.min(...Object.values(a.pricePerGram));
					const bMin = Math.min(...Object.values(b.pricePerGram));
					return aMin - bMin;
				}
				default:
					return 0;
			}
		});

		return filtered;
	}, [printers, search, materialFilter, sortBy, location]);

	const handleSelectPrinter = (id: string) => {
		setSelectedPrinter(id);
		setPage({ type: "printer", id });
	};

	const currentPrinter = useMemo(() => {
		if (page.type === "printer") return printers?.find((p) => p.id === page.id) ?? null;
		if (page.type === "order") return printers?.find((p) => p.id === page.printerId) ?? null;
		return null;
	}, [page, printers]);

	return (
		<TooltipProvider>
			<div className="min-h-screen bg-background">
				{/* Navigation */}
				<header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border">
					<div className="max-w-7xl mx-auto px-4 sm:px-6">
						<div className="flex items-center justify-between h-16">
							<button
								onClick={() => {
									setPage({ type: "discover" });
									setSelectedPrinter(null);
								}}
								className="flex items-center gap-2.5 hover:opacity-80 transition-opacity"
							>
								<div className="w-9 h-9 rounded-xl bg-foreground flex items-center justify-center">
									<Box className="w-5 h-5 text-background" />
								</div>
								<div>
									<span className="font-bold text-base tracking-tight">LocalPrint</span>
									<span className="text-xs text-muted-foreground ml-1 hidden sm:inline">
										Network
									</span>
								</div>
							</button>

							<nav className="hidden md:flex items-center gap-1">
								<Button
									variant={page.type === "discover" ? "secondary" : "ghost"}
									size="sm"
									onClick={() => {
										setPage({ type: "discover" });
										setSelectedPrinter(null);
									}}
									className="gap-2"
								>
									<MapPin className="w-4 h-4" />
									Discover
								</Button>
								<Button
									variant={page.type === "orders" ? "secondary" : "ghost"}
									size="sm"
									onClick={() => setPage({ type: "orders" })}
									className="gap-2"
								>
									<Package className="w-4 h-4" />
									My Orders
								</Button>
							</nav>

							<div className="flex items-center gap-2">
								{location && (
									<div className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 px-3 py-1.5 rounded-full">
										<MapPin className="w-3 h-3" />
										San Francisco, CA
									</div>
								)}
								<Button
									variant="ghost"
									size="sm"
									className="md:hidden"
									onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
								>
									<Menu className="w-5 h-5" />
								</Button>
							</div>
						</div>

						{mobileMenuOpen && (
							<div className="md:hidden py-3 border-t border-border flex gap-2">
								<Button
									variant={page.type === "discover" ? "secondary" : "ghost"}
									size="sm"
									onClick={() => {
										setPage({ type: "discover" });
										setSelectedPrinter(null);
										setMobileMenuOpen(false);
									}}
									className="gap-2 flex-1"
								>
									<MapPin className="w-4 h-4" />
									Discover
								</Button>
								<Button
									variant={page.type === "orders" ? "secondary" : "ghost"}
									size="sm"
									onClick={() => {
										setPage({ type: "orders" });
										setMobileMenuOpen(false);
									}}
									className="gap-2 flex-1"
								>
									<Package className="w-4 h-4" />
									My Orders
								</Button>
							</div>
						)}
					</div>
				</header>

				{/* Main content */}
				<main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
					{page.type === "discover" && (
						<div className="space-y-6">
							{/* Hero */}
							<div className="text-center py-6">
								<h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
									Find Local 3D Printers
								</h1>
								<p className="text-muted-foreground mt-2 max-w-lg mx-auto">
									Upload your 3D files and get them printed by verified makers in your
									area. Fast turnaround, quality guaranteed.
								</p>
							</div>

							{/* Search */}
							<div className="max-w-2xl mx-auto">
								<div className="relative">
									<Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
									<Input
										placeholder="Search printers, materials, specialties..."
										value={search}
										onChange={(e) => setSearch(e.target.value)}
										className="pl-10 pr-10 h-12 text-base rounded-xl"
									/>
									{search && (
										<button
											onClick={() => setSearch("")}
											className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
										>
											<X className="w-4 h-4" />
										</button>
									)}
								</div>
							</div>

							{/* Filters */}
							<div className="flex flex-wrap items-center gap-2">
								<Button
									variant="outline"
									size="sm"
									onClick={() => setShowFilters(!showFilters)}
									className="gap-2"
								>
									<SlidersHorizontal className="w-3.5 h-3.5" />
									Filters
									{materialFilter.length > 0 && (
										<Badge
											variant="secondary"
											className="ml-1 h-5 px-1.5 text-[10px]"
										>
											{materialFilter.length}
										</Badge>
									)}
								</Button>

								<Separator orientation="vertical" className="h-6" />

								{(
									[
										{ value: "distance", label: "Nearest", icon: MapPin },
										{ value: "rating", label: "Top Rated", icon: Star },
										{ value: "reputation", label: "Best Rep", icon: Shield },
										{ value: "price", label: "Lowest Price", icon: Zap },
									] as const
								).map(({ value, label, icon: Icon }) => (
									<Button
										key={value}
										variant={sortBy === value ? "secondary" : "ghost"}
										size="sm"
										onClick={() => setSortBy(value)}
										className="gap-1.5 text-xs"
									>
										<Icon className="w-3 h-3" />
										{label}
									</Button>
								))}

								<div className="ml-auto text-xs text-muted-foreground">
									{filteredPrinters.length} printers found
								</div>
							</div>

							{showFilters && (
								<Card>
									<CardContent className="p-4">
										<div className="flex items-center justify-between mb-3">
											<h3 className="text-sm font-medium flex items-center gap-2">
												<Layers className="w-4 h-4 text-muted-foreground" />
												Filter by Material
											</h3>
											{materialFilter.length > 0 && (
												<Button
													variant="ghost"
													size="sm"
													onClick={() => setMaterialFilter([])}
													className="text-xs h-7"
												>
													Clear all
												</Button>
											)}
										</div>
										<div className="flex flex-wrap gap-2">
											{allMaterials.map((mat) => (
												<Button
													key={mat}
													variant={
														materialFilter.includes(mat) ? "default" : "outline"
													}
													size="sm"
													onClick={() =>
														setMaterialFilter((prev) =>
															prev.includes(mat)
																? prev.filter((m) => m !== mat)
																: [...prev, mat]
														)
													}
													className="text-xs"
												>
													{mat}
												</Button>
											))}
										</div>
									</CardContent>
								</Card>
							)}

							{/* Map */}
							<MapView
								printers={filteredPrinters}
								userLocation={location}
								selectedPrinter={selectedPrinter}
								onSelectPrinter={handleSelectPrinter}
							/>

							{/* Printer list */}
							<div>
								<h2 className="text-lg font-semibold mb-4">
									{search || materialFilter.length > 0
										? "Matching Printers"
										: "Printers Near You"}
								</h2>
								{loading ? (
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										{[1, 2, 3, 4].map((i) => (
											<Card key={i}>
												<CardContent className="p-4">
													<div className="animate-pulse flex gap-4">
														<div className="w-16 h-16 rounded-xl bg-muted" />
														<div className="flex-1 space-y-2">
															<div className="h-4 bg-muted rounded w-3/4" />
															<div className="h-3 bg-muted rounded w-1/2" />
															<div className="h-3 bg-muted rounded w-2/3" />
														</div>
													</div>
												</CardContent>
											</Card>
										))}
									</div>
								) : filteredPrinters.length === 0 ? (
									<Card>
										<CardContent className="p-12 text-center">
											<Printer className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
											<p className="font-medium">No printers found</p>
											<p className="text-sm text-muted-foreground mt-1">
												Try adjusting your search or filters
											</p>
										</CardContent>
									</Card>
								) : (
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										{filteredPrinters.map((printer) => (
											<PrinterCard
												key={printer.id}
												printer={printer}
												distance={
													location
														? getDistance(
																location.lat,
																location.lng,
																printer.location.lat,
																printer.location.lng
														  )
														: undefined
												}
												onSelect={handleSelectPrinter}
												isSelected={selectedPrinter === printer.id}
											/>
										))}
									</div>
								)}
							</div>

							{/* Platform stats */}
							<div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4">
								{[
									{ label: "Verified Printers", value: "150+", icon: Shield },
									{ label: "Prints Completed", value: "12,400+", icon: Package },
									{ label: "Avg Rating", value: "4.8", icon: Star },
									{ label: "Cities Covered", value: "45+", icon: MapPin },
								].map(({ label, value, icon: Icon }) => (
									<Card key={label}>
										<CardContent className="p-4 text-center">
											<Icon className="w-5 h-5 mx-auto text-muted-foreground mb-1.5" />
											<div className="text-xl font-bold">{value}</div>
											<div className="text-xs text-muted-foreground">{label}</div>
										</CardContent>
									</Card>
								))}
							</div>
						</div>
					)}

					{page.type === "printer" && currentPrinter && (
						<PrinterProfilePage
							printer={currentPrinter}
							onBack={() => {
								setPage({ type: "discover" });
								setSelectedPrinter(null);
							}}
							onRequestPrint={(id) => setPage({ type: "order", printerId: id })}
						/>
					)}

					{page.type === "order" && currentPrinter && (
						<OrderPage
							printer={currentPrinter}
							onBack={() =>
								setPage({ type: "printer", id: currentPrinter.id })
							}
							onOrderCreated={() => setPage({ type: "orders" })}
						/>
					)}

					{page.type === "orders" && (
						<OrderTracking
							onWriteReview={(orderId, printerId) =>
								setPage({ type: "review", orderId, printerId })
							}
						/>
					)}

					{page.type === "review" && (
						<ReviewForm
							orderId={page.orderId}
							printerId={page.printerId}
							onBack={() => setPage({ type: "orders" })}
							onSubmitted={() => setPage({ type: "orders" })}
						/>
					)}
				</main>

				{/* Footer */}
				<footer className="border-t border-border mt-12">
					<div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
						<div className="flex flex-col sm:flex-row items-center justify-between gap-4">
							<div className="flex items-center gap-2.5">
								<div className="w-7 h-7 rounded-lg bg-foreground flex items-center justify-center">
									<Box className="w-4 h-4 text-background" />
								</div>
								<span className="font-semibold text-sm">LocalPrint Network</span>
							</div>
							<div className="flex items-center gap-6 text-xs text-muted-foreground">
								<span>Secure Escrow Payments</span>
								<span>Verified Printers</span>
								<span>Quality Guaranteed</span>
							</div>
						</div>
					</div>
				</footer>
			</div>
		</TooltipProvider>
	);
}

export default App;
